import type { SpaceType } from "app/models/shared-types";
import { get } from "svelte/store";
import { page } from "$app/stores";
import { redirect } from "@sveltejs/kit";
import type { LayoutRouteId } from "../routes/$types";
import { resolveRoute } from "$app/paths";
import { CommunitiesLlmSessionsListSourceEnum } from "@outverse-video/outverse-api-client-lib";
import { PUBLIC_BASE_FRONTEND_URL, PUBLIC_ENV } from "$env/static/public";

export type ValidLayoutRouteId = NonNullable<LayoutRouteId>;

export const publicRoutes: ValidLayoutRouteId[] = [
  "/[community=slug]/forums/[forumSlug]/[uuid]", // forum + thread pages
  "/[community=slug]/forums/page/[slug]/[uuid]", // custom space page
  "/[community=slug]/(knowledge-base)/doc", // documents
  "/[community=slug]/insights", // analytics
  "/[community=slug]/content-library", // content library
];

export const sidebarRouteIds: ValidLayoutRouteId[] = [
  "/[community=slug]/forums",
  "/[community=slug]/(knowledge-base)/doc",
  "/[community=slug]/(knowledge-base)/category/[slug]/[uuid]",
  "/[community=slug]/(knowledge-base)/section/[slug]/[uuid]",
  "/[community=slug]/settings",
  "/[community=slug]/agents",
];

const testDomains: Array<[string, string[]]> = [
  // test domains
  ["unittestcustomdomain", ["help.unittestcustomdomain.com"]],
  ["unittestsubdirdomain", ["www.unittestsubdirdomain.com"]],
];

const customDomains: Array<[string, string[]]> = [
  // prod test domain
  ["joetestcommunity", ["test.joeoutversedomaintest.com"]],
  // staging test domain
  ["aikido-redirects", ["teststag.joeoutversedomaintest.com"]],
  ["ownverse", ["app.localhost"]],

  // customers
  ["mmostatus", ["community.mmostatus.com"]],
  ["sonr", ["universe.sonr.io"]],
  ["sonr-community", ["community.sonr.io"]],
  ["askui", ["community.askui.com"]],
  ["enernite", ["community.enernite.com"]],
  ["sauce-labs", ["community.saucelabs.com"]],
  ["strabo", ["community.strabo.app"]],
  ["aerodyne", ["codex.dronos.ai"]],
  ["godmodehq", ["society.godmodehq.com"]],
  ["budget-mentor", ["community.budget-mentor.com"]],
  ["menschenbuchenfotografen", ["successhub.stevebaka.de"]],
  ["contingent", ["support.contingent.eco"]],
  ["outverse", ["support.outverse.com"]],
  ["spektr", ["discover.spektr.com"]],
  ["hiphops", ["developers.hiphops.io"]],
  ["spline", ["docs.spline.design"]],
  ...(PUBLIC_ENV !== "development"
    ? [["outverse-demo-community", ["stag.outverse.xyz"]] as [string, string[]]]
    : []), // exclude from development
  // subdir hosting customers
  ["rive", ["rive.app", "rive-two.vercel.app"]],
  // test subdir hosting community
  ["subdir-test-prod", ["www.outverse-subdir-test-prod.com", "prod-subdir-host-test.vercel.app"]],
  ["subdir-test-stag", ["www.outverse-subdir-test-stag.com", "stag-subdir-host-test.vercel.app"]],
  // the review app has to be present otherwise getSubdomainInfo(url.hostname) will fail
  // in hooks.ts::reroute, because there the url is that of the review app
  // [
  //   "outverse-demo-community",
  //   [
  //     "www.joeoutversesubdirectorydomain.com",
  //     "community-video-git-feature-eng-1547-subdirecto-38fd35-outverse.vercel.app",
  //   ],
  // ],
];

const domains = PUBLIC_ENV === "test" ? customDomains.concat(testDomains) : customDomains;

// eg. "subdir-test-stag": ["www.outverse-subdir-test-stag.com", "stag-subdir-host-test.vercel.app"]
export const communityDomainsMap = new Map<string, string[]>(domains);

// eg. "www.outverse-subdir-test-stag.com": "subdir-test-stag", "stag-subdir-host-test.vercel.app": "subdir-test-stag"
const customDomainsMap = new Map<string, string>(
  domains
    .map(([domain, slug]) => {
      if (Array.isArray(slug)) {
        return slug.map((s) => [s, domain]);
      }
      return [slug, domain];
    })
    .flat() as Array<[string, string]>
);

const testPath = new Map<string, string>([["unittestsubdirdomain", "forums-docs"]]);

const subdirHostingDefaultPaths = new Map<string, string>([
  // ["a-private-com", "mypath"],
  ["subdir-test-prod", "subdir"],
  ["subdir-test-stag", "subdir"],
  ["rive", "community"],
]);

export const subdirHostingPaths =
  PUBLIC_ENV === "test"
    ? new Map([...subdirHostingDefaultPaths, ...testPath])
    : subdirHostingDefaultPaths;

export const convertToCanonicalUrl = (url: URL) => {
  /*
   * If the url is not for a custom domain, just return it.
   * If it is, then it might be the case that we are dealing with a case of 'path hosting'.
   * In that case, the community slug will map to 2 domains - first the 'canonical' host that
   * will be seen by the user in the browser (the proxy host), and second the underlying host of the vercel app.
   * The purpose of this function is to always return the canonical host.
   * */
  const newUrl = new URL(url);
  const communitySlug = customDomainsMap.get(url.hostname);
  if (!communitySlug) {
    return newUrl;
  }
  const canonicalHost = communityDomainsMap.get(communitySlug)![0];
  newUrl.hostname = canonicalHost;
  newUrl.port = "";
  return newUrl;
};

/** Redirects to the custom domain if the community has one.
 *
 * There should only be a single canonical url for any community.
 */
export const redirectCustomDomains = (
  route: { id: LayoutRouteId },
  url: URL,
  communitySlug: string
) => {
  const customDomains = communityDomainsMap.get(communitySlug);
  if (customDomains) {
    for (const customDomain of customDomains) {
      if (url.hostname === customDomain) {
        return;
      }
    }

    const newUrl = new URL(url);
    newUrl.hostname = customDomains[0];
    newUrl.port = "";

    const pathPrefix = subdirHostingPaths.get(communitySlug);
    const subdir = pathPrefix ? `/${pathPrefix}` : "";

    if (route.id?.split("/")[1] === "[community=slug]") {
      const parts = newUrl.pathname.split("/");
      newUrl.pathname = `${subdir}/${parts.slice(2).join("/")}`;
    } else {
      newUrl.pathname = `${subdir}/${newUrl.pathname}`;
    }

    redirect(308, newUrl);
  }
};

export const getSubdomainInfo = (hostname: string) => {
  const communitySlug = customDomainsMap.get(hostname);
  const pathPrefix = communitySlug && subdirHostingPaths.get(communitySlug);

  return {
    communitySlug,
    usingSubdomain: !!communitySlug,
    pathPrefix,
  };
};

export interface CommunityParams {
  communitySlug: string;
}

export interface CommunitySettingsParams extends CommunityParams {
  section: string;
  subSection: string;
}

export interface DefaultNavItemParams extends CommunityParams {
  path: string;
}

export interface SpaceParams extends CommunityParams {
  spaceType: SpaceType;
  spaceUuid: string;
  spaceSlug: string;
}

export interface DocParams extends CommunityParams {
  docUuid: string;
  docSlug: string;
}

export interface SectionParams extends CommunityParams {
  sectionUuid: string;
  sectionSlug: string;
}

export interface CategoryParams extends CommunityParams {
  categoryUuid: string;
  categorySlug: string;
}

export interface ConversationParams extends CommunityParams {
  conversationId: string;
}

export interface ThreadParams extends Omit<SpaceParams, "spaceType"> {
  threadSlug: string;
  threadUuid: string;
}

export interface ReplyParams extends ThreadParams {
  replySeqNum: string;
}

export interface ChangelogParams extends CommunityParams {
  postSlug: string;
  postUuid: string;
}

export interface SnippetParams extends CommunityParams {
  snippetSlug: string;
  snippetUuid: string;
}

export interface SessionParams extends CommunityParams {
  id: string;
}

export interface WidgetParams extends CommunityParams {
  conversationId: string;
}

const SVELTEKIT_API_URL = "/api";
const WORKSPACE_ROOT_PATH = "/[communitySlug]";
const WORKSPACE_PATH = WORKSPACE_ROOT_PATH + "/workspace";
const COMMUNITY_ROOT_PATH = WORKSPACE_ROOT_PATH + "/forums";
const COMMUNITY_HOME_PATH = COMMUNITY_ROOT_PATH + "/home";
const WIDGETVERSE_PATH = "/widgetverse/[communitySlug].js";

// workspace

//outverse-agent
const CHAT_PATH = "/chat";
const AGENTS_ROOT_PATH = WORKSPACE_ROOT_PATH + "/agents";
const AGENT_SANDBOX_PATH = AGENTS_ROOT_PATH + CHAT_PATH + "/sandbox";
const AGENT_CONVERSATION_HISTORY_PATH = AGENTS_ROOT_PATH + CHAT_PATH + "/conversation-history";
const AGENT_CONVERSATION_PATH = AGENT_CONVERSATION_HISTORY_PATH + "/[conversationId]";
const AGENT_INTENTS_PATH = AGENTS_ROOT_PATH + CHAT_PATH + "/intents";
const AGENT_ESCALATION_PATH = AGENTS_ROOT_PATH + CHAT_PATH + "/escalations";
const AGENT_SETTINGS_PATH = AGENTS_ROOT_PATH + CHAT_PATH + "/settings";
const AGENT_ERROR_LOGS_PATH = AGENTS_ROOT_PATH + CHAT_PATH + "/agent-logs";

// rest of the routes
const FULL_PAGE_PROTOTYPE_PATH = WORKSPACE_ROOT_PATH + "/beta-sandbox";
const CONTEXT_SOURCES_PATH = WORKSPACE_ROOT_PATH + "/context-sources";
const EXTERNAL_SOURCES_PATH = CONTEXT_SOURCES_PATH + "/external";
const SNIPPETS_ROOT_PATH = CONTEXT_SOURCES_PATH + "/snippets";
const SNIPPET_PATH = SNIPPETS_ROOT_PATH + "/[snippetSlug]/[snippetUuid]";
const CHANGELOG_ROOT_PATH = WORKSPACE_ROOT_PATH + "/changelog";
const CHANGELOG_PATH = CHANGELOG_ROOT_PATH + "/[postSlug]/[postUuid]";
const SPACE_PATH = COMMUNITY_ROOT_PATH + "/[spaceSlug]/[spaceUuid]";
const CUSTOM_SPACE_PATH = COMMUNITY_ROOT_PATH + "/page/[spaceSlug]/[spaceUuid]";
const THREAD_PATH = SPACE_PATH + "/[threadSlug]/[threadUuid]";
const REPLY_PATH = THREAD_PATH + "?post=[replySeqNum]";
const DOC_ROOT_PATH = WORKSPACE_ROOT_PATH + "/doc";
const DOC_PATH = DOC_ROOT_PATH + "/[docSlug]/[docUuid]";
const SECTION_ROOT_PATH = WORKSPACE_ROOT_PATH + "/section/";
const SECTION_PATH = SECTION_ROOT_PATH + "[sectionSlug]/[sectionUuid]";
const CATEGORY_ROOT_PATH = WORKSPACE_ROOT_PATH + "/category/";
const CATEGORY_PATH = CATEGORY_ROOT_PATH + "[categorySlug]/[categoryUuid]";
const INSIGHTS_ROOT_PATH = WORKSPACE_ROOT_PATH + "/insights";
const CONTENT_LIBRARY_PATH = WORKSPACE_ROOT_PATH + "/content-library";
const COMMUNITY_ERROR_PATH = COMMUNITY_ROOT_PATH + "/community-error";
const COMMUNITY_EDIT_PATH = WORKSPACE_ROOT_PATH + "/settings/[section]/[subSection]";
const COMMUNITY_JOIN_PATH = WORKSPACE_ROOT_PATH + "/join";
const COMMUNITY_MEMBERS_PATH = COMMUNITY_ROOT_PATH + "/members";
const COMMUNITY_DEFAULT_NAV_ITEM_PATH = COMMUNITY_ROOT_PATH + "/[path]";

// internal routes
const OBSERVATORY_PATH = WORKSPACE_ROOT_PATH + "/observatory";

// email agent
const EMAIL_PATH = "/email";
const EMAIL_AGENT_EXECUTION_HISTORY_PATH = AGENTS_ROOT_PATH + "/agent-execution-history";
const EMAIL_CONVERSATION_HISTORY_PATH = AGENTS_ROOT_PATH + EMAIL_PATH + "/conversation-history";
const EMAIL_CONVERSATION_PATH = EMAIL_CONVERSATION_HISTORY_PATH + "/[conversationId]";
const EMAIL_INTENTS_PATH = AGENTS_ROOT_PATH + EMAIL_PATH + "/intents";
const EMAIL_SETTINGS_PATH = AGENTS_ROOT_PATH + EMAIL_PATH + "/settings";
const EMAIL_INSIGHTS_PATH = AGENTS_ROOT_PATH + EMAIL_PATH + "/insights";
const EMAIL_ESCALATIONS_PATH = AGENTS_ROOT_PATH + EMAIL_PATH + "/escalations";
const EMAIL_ERROR_LOGS_PATH = AGENTS_ROOT_PATH + EMAIL_PATH + "/agent-logs";

// healthcheck
const HEALTHCHECK_DIAGNOSE_PATH = "/healthcheck/diagnose";

// user
const USER_HOME_PATH = "/user-home";
const USER_PROFILE_PATH = "/user-profile";
const USER_PROFILE_OVERVIEW_PATH = USER_PROFILE_PATH + "/overview";
const USER_PROFILE_PROFILE_PATH = USER_PROFILE_PATH + "/profile";
const USER_PROFILE_ACCOUNT_PATH = USER_PROFILE_PATH + "/account";
const VERIFY_EMAIL_PATH = "/verify-email";

// auth
const LOGIN_PATH = "/login";
const SIGN_UP_PATH = "/sign-up";

// external apps
const ZENDESK_APP_INIT_PATH = "/zendesk_app";
const ZENDESK_APP_PATH = ZENDESK_APP_INIT_PATH + "/[communitySlug]/copilot";
const CHROME_EXTENSION_APP_INIT_PATH = "/chrome-extension";
const CHROME_EXTENSION_APP_PATH = "/[communitySlug]/widget?isChromeExtension=true";

// widget
const WIDGET_HOME_PATH = WORKSPACE_ROOT_PATH + "/widget/home";
const WIDGET_CONVERSATION_HISTORY_PATH = WORKSPACE_ROOT_PATH + "/widget/conversation-history";
const WIDGET_CONVERSATION_ROOT_PATH = WORKSPACE_ROOT_PATH + "/widget/conversation";
const WIDGET_CONVERSATION_PATH = WIDGET_CONVERSATION_ROOT_PATH + "/[conversationId]";

const _origin = (add: boolean | undefined) => (add ? window.location.origin : "");

export const processPath = (
  path: string,
  params?: any,
  absolute?: boolean,
  url?: URL,
  nonCommunityUrl?: boolean
) => {
  const hostname = (url || get(page)!.url)?.hostname;
  const { usingSubdomain } = getSubdomainInfo(hostname);

  if (usingSubdomain && !nonCommunityUrl) {
    path = path.replace(/\/\[communitySlug\]/, "");
    if (!path) path = "/";
  }
  if (!path) path = "/";

  return _origin(absolute) + resolveRoute(path, params);
};

export const spaceURL = (params: SpaceParams, absolute?: boolean, url?: URL) => {
  if (params.spaceType === "custom") {
    return processPath(CUSTOM_SPACE_PATH, params, absolute, url);
  }

  return processPath(SPACE_PATH, params, absolute, url);
};

export const sveltekitApiUrl = () => resolveRoute(SVELTEKIT_API_URL, {});

export const rootUrl = (absolute?: boolean, url?: URL) => processPath("/", {}, absolute, url, true);

export const workspaceRootUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(WORKSPACE_ROOT_PATH, params, absolute, url);

export const loginUrl = (absolute?: boolean, url?: URL) =>
  processPath(LOGIN_PATH, {}, absolute, url, true);

export const signUpUrl = (absolute?: boolean, url?: URL) =>
  processPath(SIGN_UP_PATH, {}, absolute, url);

export const authUrl = (absolute?: boolean, url?: URL) =>
  processPath("/auth", {}, absolute, url, true);

export const widgetHomeUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(WIDGET_HOME_PATH, params, absolute, url);

export const widgetConversationHistoryUrl = (
  params: CommunityParams,
  absolute?: boolean,
  url?: URL
) => processPath(WIDGET_CONVERSATION_HISTORY_PATH, params, absolute, url);

export const widgetConversationRootUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(WIDGET_CONVERSATION_ROOT_PATH, params, absolute, url);

export const widgetConversationUrl = (params: WidgetParams, absolute?: boolean, url?: URL) =>
  processPath(WIDGET_CONVERSATION_PATH, params, absolute, url);

export const zendeskAppUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(ZENDESK_APP_PATH, params, absolute, url, true);

export const zendeskAppInitUrl = (absolute?: boolean, url?: URL) =>
  processPath(ZENDESK_APP_INIT_PATH, {}, absolute, url, true);

export const chromeExtensionAppUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(CHROME_EXTENSION_APP_PATH, params, absolute, url, true);

export const chromeExtensionAppInitUrl = (absolute?: boolean, url?: URL) =>
  processPath(CHROME_EXTENSION_APP_INIT_PATH, {}, absolute, url, true);

export const healthcheckDiagnoseUrl = (absolute?: boolean, url?: URL) =>
  processPath(HEALTHCHECK_DIAGNOSE_PATH, {}, absolute, url, true);

export const verifyEmailUrl = (absolute?: boolean, url?: URL) =>
  processPath(VERIFY_EMAIL_PATH, {}, absolute, url, true);

export const userProfileUrl = (absolute?: boolean, url?: URL) =>
  processPath(USER_PROFILE_PATH, {}, absolute, url, true);

export const userProfileOverviewUrl = (absolute?: boolean, url?: URL) =>
  processPath(USER_PROFILE_OVERVIEW_PATH, {}, absolute, url, true);

export const userProfileProfileUrl = (absolute?: boolean, url?: URL) =>
  processPath(USER_PROFILE_PROFILE_PATH, {}, absolute, url, true);

export const userProfileAccountUrl = (absolute?: boolean, url?: URL) =>
  processPath(USER_PROFILE_ACCOUNT_PATH, {}, absolute, url, true);

export const communityRootUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(COMMUNITY_ROOT_PATH, params, absolute, url);

export const communityHomeUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(COMMUNITY_HOME_PATH, params, absolute, url);

export const changelogRootURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(CHANGELOG_ROOT_PATH, params, absolute, url);

export const contextSourcesURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(CONTEXT_SOURCES_PATH, params, absolute, url);

export const externalSourcesURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(EXTERNAL_SOURCES_PATH, params, absolute, url);

export const snippetsRootURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(SNIPPETS_ROOT_PATH, params, absolute, url);

export const snippetURL = (
  params: SnippetParams,
  pageNumber?: number,
  absolute?: boolean,
  url?: URL
) => {
  let path = processPath(SNIPPET_PATH, params, absolute, url);

  if (pageNumber !== undefined) {
    const urlObj = new URL(path, url || get(page)!.url);
    urlObj.searchParams.set("page", pageNumber.toString());
    path = urlObj.pathname + urlObj.search;
  }
  return path;
};

export const fullPagePrototypeURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(FULL_PAGE_PROTOTYPE_PATH, params, absolute, url);

export const changelogURL = (params: ChangelogParams, absolute?: boolean, url?: URL) =>
  processPath(CHANGELOG_PATH, params, absolute, url);

export const threadURL = (params: ThreadParams, absolute?: boolean, url?: URL) =>
  processPath(THREAD_PATH, { ...params, spaceType: "forum" }, absolute, url);

export const replyUrl = (params: ReplyParams, absolute?: boolean, url?: URL) =>
  processPath(REPLY_PATH, { ...params, spaceType: "forum" }, absolute, url);

export const contentLibraryUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(CONTENT_LIBRARY_PATH, { ...params }, absolute, url);

export const isBackLinkInternal = (): boolean => !!window.history.state?.usr?.internal;

export const manageMembersUrls = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath("/[communitySlug]/edit/members/", params, absolute, url);

export const workspaceUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(WORKSPACE_PATH, params, absolute, url);

export const docRootURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(DOC_ROOT_PATH, params, absolute, url);

export const docURL = (params: DocParams, absolute?: boolean, url?: URL) =>
  processPath(DOC_PATH, params, absolute, url);

export const sectionRootURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(SECTION_ROOT_PATH, params, absolute, url);

export const agentsRootURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(AGENTS_ROOT_PATH, params, absolute, url);

export const chatAgentIntentsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(AGENT_INTENTS_PATH, params, absolute, url);

export const sectionURL = (params: SectionParams, absolute?: boolean, url?: URL) =>
  processPath(SECTION_PATH, params, absolute, url);

export const categoryRootURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(CATEGORY_ROOT_PATH, params, absolute, url);

export const categoryURL = (params: CategoryParams, absolute?: boolean, url?: URL) =>
  processPath(CATEGORY_PATH, params, absolute, url);

export const observatoryURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(OBSERVATORY_PATH, params, absolute, url);

export const conversationHistoryURL = (
  params: CommunityParams,
  source: "chat" | "email",
  absolute?: boolean,
  url?: URL
) => {
  const path = source == "chat" ? AGENT_CONVERSATION_HISTORY_PATH : EMAIL_CONVERSATION_HISTORY_PATH;
  return processPath(path, params, absolute, url);
};

export const conversationHistoryURLWithSearch = (
  params: CommunityParams,
  source: "chat" | "email",
  absolute?: boolean,
  url?: URL
) => {
  let path = source == "chat" ? AGENT_CONVERSATION_HISTORY_PATH : EMAIL_CONVERSATION_HISTORY_PATH;
  path = processPath(path, params, absolute, url);
  const currentUrl = url || get(page)!.url;
  return path + currentUrl.search;
};

export const conversationUrl = (
  params: ConversationParams,
  source: CommunitiesLlmSessionsListSourceEnum,
  pageNumber?: number,
  absolute?: boolean,
  url?: URL
) => {
  let path =
    source == CommunitiesLlmSessionsListSourceEnum.Widget
      ? AGENT_CONVERSATION_PATH
      : EMAIL_CONVERSATION_PATH;
  path = processPath(path, params, absolute, url);
  const urlObj = new URL(path, url || get(page)!.url);

  // preserve existing params
  const currentUrl = url || get(page)!.url;
  currentUrl.searchParams.forEach((value, key) => {
    if (key !== "page") {
      // if the parameter already exists in the target URL, append to preserve multiple values
      if (urlObj.searchParams.has(key)) {
        urlObj.searchParams.append(key, value);
      } else {
        urlObj.searchParams.set(key, value);
      }
    }
  });

  if (pageNumber !== undefined) {
    urlObj.searchParams.set("page", pageNumber.toString());
  }

  return urlObj.pathname + urlObj.search;
};

export const emailAgentConversationHistoryUrl = (
  params: CommunityParams,
  absolute?: boolean,
  url?: URL
) => processPath(EMAIL_CONVERSATION_HISTORY_PATH, params, absolute, url);

export const chatAgentEscalationUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(AGENT_ESCALATION_PATH, params, absolute, url);

export const chatAgentErrorLogsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(AGENT_ERROR_LOGS_PATH, params, absolute, url);

export const emailIntentsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(EMAIL_INTENTS_PATH, params, absolute, url);

export const emailAgentInsightsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(EMAIL_INSIGHTS_PATH, params, absolute, url);

export const emailAgentEscalationsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(EMAIL_ESCALATIONS_PATH, params, absolute, url);

export const emailAgentErrorLogsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(EMAIL_ERROR_LOGS_PATH, params, absolute, url);

export const emailAgentSettingsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(EMAIL_SETTINGS_PATH, params, absolute, url);

export const emailAgentExecutionHistoryUrl = (
  params: CommunityParams,
  absolute?: boolean,
  url?: URL
) => processPath(EMAIL_AGENT_EXECUTION_HISTORY_PATH, params, absolute, url);

export const emailConversationUrl = (
  params: ConversationParams,
  pageNumber?: number,
  absolute?: boolean,
  url?: URL
) => {
  const path = processPath(EMAIL_CONVERSATION_PATH, params, absolute, url);
  const urlObj = new URL(path, url || get(page)!.url);

  // preserve existing params
  const currentUrl = url || get(page)!.url;
  currentUrl.searchParams.forEach((value, key) => {
    if (key !== "page") urlObj.searchParams.set(key, value);
  });

  if (pageNumber !== undefined) {
    urlObj.searchParams.set("page", pageNumber.toString());
  }

  return urlObj.pathname + urlObj.search;
};

export const agentSandboxURL = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(AGENT_SANDBOX_PATH, params, absolute, url);

export const communityErrorUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(COMMUNITY_ERROR_PATH, params, absolute, url);

export const communitySettingsURL = (
  params: CommunitySettingsParams,
  absolute?: boolean,
  url?: URL
) => processPath(COMMUNITY_EDIT_PATH, params, absolute, url);

export const communitySwitcherHomeUrl = (params: CommunityParams) => {
  // this only gets shown if not already on a subdomain. So apart from standard base platform urls, we
  // only need to handle the case where we are on the base platform, and might be navigating away from it.
  const customDomainHosts = communityDomainsMap.get(params.communitySlug);
  if (!customDomainHosts) {
    return workspaceRootUrl(params);
  }

  const protocol = get(page)!.url.protocol;
  const host = customDomainHosts[0];
  const { pathPrefix } = getSubdomainInfo(host);
  const subdir = pathPrefix ? `/${pathPrefix}` : "";
  return `${protocol}//${host}${subdir}/`;
};

export const homeUrl = (url?: URL) => {
  const hostname = (url || get(page)!.url).hostname;
  const { usingSubdomain, communitySlug } = getSubdomainInfo(hostname);

  if (usingSubdomain) {
    return workspaceRootUrl({ communitySlug: communitySlug! }, false, url);
  }

  return processPath(USER_HOME_PATH, {}, false, url, true);
};

export const userHomeUrl = (url?: URL) => processPath(USER_HOME_PATH, {}, false, url, true);

export const insightsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) => {
  return processPath(INSIGHTS_ROOT_PATH, params, absolute, url);
};

export const chatAgentSettingsUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(AGENT_SETTINGS_PATH, params, absolute, url);

export const basePlatformUserHomeUrl = (url?: URL) => {
  // Sometimes user-home is wanted because it is the 'home' url for the platform, for example after logging in.
  // In those cases, this function should not be used. If `usingSubdomain` is true, then requesting user-home directly
  // will automatically redirect to the community home. However, if the user-home is wanted for another reason, ie. to
  // do something that is specifically found on the user-home (eg. as of writing just creating a new community), then
  // this function should be used, since it will allow the user to access user-home by redirecting to the base platform
  // user home.

  const hostname = (url || get(page)!.url).hostname;
  const { usingSubdomain } = getSubdomainInfo(hostname);

  if (usingSubdomain) {
    const basePlatformUrl = `${PUBLIC_BASE_FRONTEND_URL}${USER_HOME_PATH}`;
    window.location.href = basePlatformUrl;
    return basePlatformUrl;
  }

  return processPath(USER_HOME_PATH, {}, false, url, true);
};

export const communityJoinUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(COMMUNITY_JOIN_PATH, params, absolute, url);

export const communityMembersUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(COMMUNITY_MEMBERS_PATH, params, absolute, url);

export const widgetverseUrl = (params: CommunityParams, absolute?: boolean, url?: URL) =>
  processPath(WIDGETVERSE_PATH, params, absolute, url, true);

export const communityDefaultNavItemUrl = (
  params: DefaultNavItemParams,
  absolute?: boolean,
  url?: URL
) => processPath(COMMUNITY_DEFAULT_NAV_ITEM_PATH, params, absolute, url);

/**
 * Returns the base application URL for the given URL.
 * The base application URL includes the protocol and the host with an optional path prefix.
 * This accounts for custom domains and subdirectory hosting.
 *
 * @example
 * const url = new URL("https://www.testsubdirdomain.com/forums-docs/forums/general/abc123/tidy-title/abc123");
 * const result = baseAppUrl(url);
 * console.log(result); // "https://www.testsubdirdomain.com/forums-docs"
 *
 * @param url - The URL for which to generate the base application URL.
 * @returns The base application URL.
 */
export const baseAppUrl = (url: URL) => {
  const { pathPrefix } = getSubdomainInfo(url.hostname);
  const path = `${convertToCanonicalUrl(url).host}${pathPrefix ? "/" + pathPrefix : ""}`;
  return `${url.protocol}//${path}`;
};
